/**
 * Tournament match board — admin popup + venue TV (/tv/tournaments/.../board).
 */
import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { fireTv180Confetti } from '../../public/tv-180-celebration.js';

function parseTournamentMatchIds() {
  const parts = (globalThis.location?.pathname || '').split('/').filter(Boolean);
  const ti = parts.indexOf('tournaments');
  const mi = parts.indexOf('matches');
  if (ti < 0 || !parts[ti + 1] || mi < 0 || !parts[mi + 1]) {
    return { tournamentId: '', matchId: '' };
  }
  return {
    tournamentId: decodeURIComponent(parts[ti + 1]),
    matchId: decodeURIComponent(parts[mi + 1]),
  };
}

function isTvPublicBoardPath() {
  try {
    const p = (globalThis.location?.pathname || '').replace(/\/+$/, '') || '/';
    return /^\/tv\/tournaments\/[^/]+\/matches\/[^/]+\/board$/.test(p);
  } catch {
    return false;
  }
}

function isKioskBoardMode() {
  try {
    if (isTvPublicBoardPath()) return true;
    return new URLSearchParams(globalThis.location?.search || '').get('kiosk') === '1';
  } catch {
    return false;
  }
}

function tvTokenFromLocation() {
  try {
    return String(new URLSearchParams(globalThis.location?.search || '').get('tv_token') || '').trim();
  } catch {
    return '';
  }
}

function apiAbsoluteUrl(path) {
  const base = String(globalThis.__API_BASE__ || '').replace(/\/+$/, '');
  const p = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  return base ? `${base}${p}` : p;
}

function applyDedicatedChromeMode(on) {
  try {
    globalThis.Alpine?.store?.('ui')?.setHideChrome?.(!!on);
    const shell = document.getElementById('admin');
    if (shell) shell.classList.toggle('layout-scorer-bleed', !!on);
    const view = document.getElementById('view');
    if (view) view.classList.toggle('layout-scorer-bleed', !!on);
  } catch { /* ignore */ }
}

Alpine.data('tournamentMatchBoard', () => ({
  loading: true,
  error: '',
  tournamentId: '',
  matchId: '',
  match: null,
  session: null,
  eventSource: null,
  eventStreamHealthy: false,
  fullscreenActive: false,
  kioskMode: false,
  _tvToken: '',
  _pollTimer: null,
  _tv180Seen: null,
  _tv180BaselineSeeded: false,
  _tv180CloseTimer: null,
  tv180Active: false,
  tv180Name: 'Player',
  tv180Avatar: '/assets/placeholder-player.png',
  matchWinnerOverlayVisible: false,

  async init() {
    applyDedicatedChromeMode(true);
    this.kioskMode = isKioskBoardMode();
    const { tournamentId, matchId } = parseTournamentMatchIds();
    this.tournamentId = tournamentId;
    this.matchId = matchId;
    if (!tournamentId || !matchId) {
      this.error = 'Invalid board URL';
      this.loading = false;
      return;
    }
    this._tvToken = tvTokenFromLocation();
    try {
      await this.loadBoard();
      this.connectEventStream();
      if (this.kioskMode) this.seedTv180Baseline();
    } catch (e) {
      this.error = e.message || String(e);
    } finally {
      this.loading = false;
      this.syncMatchWinnerOverlay();
      Alpine.nextTick(() => this.maybeCelebrate180());
    }
    this.handleFullscreenChange = () => {
      this.fullscreenActive = !!document.fullscreenElement;
    };
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    window.addEventListener('beforeunload', () => this.teardown());
  },

  teardown() {
    applyDedicatedChromeMode(false);
    this.disconnectEventStream();
    if (this._pollTimer) globalThis.clearInterval(this._pollTimer);
    if (this._tv180CloseTimer) {
      globalThis.clearTimeout(this._tv180CloseTimer);
      this._tv180CloseTimer = null;
    }
    if (this.handleFullscreenChange) {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    }
  },

  isVenueTvBoard() {
    return this.kioskMode && isTvPublicBoardPath();
  },

  liveScoring() {
    return this.session?.liveScoring || {};
  },

  format() {
    return this.match?.format || this.session?.format || {};
  },

  participant(side) {
    return (this.match?.participants || []).find((p) => p.side === side) || null;
  },

  participantName(side) {
    return this.participant(side)?.displayName || (side === 'A' ? 'Player A' : 'Player B');
  },

  participantAvatar(side) {
    const p = this.participant(side);
    return (
      p?.imageUrl
      || p?.photo
      || p?.avatar
      || p?.image
      || '/assets/placeholder-player.png'
    );
  },

  remaining(side) {
    const ls = this.liveScoring();
    if (ls.matchFinished) return '—';
    return Number(ls.currentRemaining?.[side] ?? ls.startingScore ?? 501);
  },

  legsWon(side) {
    const ls = this.liveScoring();
    return side === 'A' ? Number(ls.legsWonA) || 0 : Number(ls.legsWonB) || 0;
  },

  legsNeededToWin() {
    const bo = Math.max(1, Number(this.format().bestOfLegs) || 3);
    return Math.ceil(bo / 2);
  },

  formatBestOfLabel() {
    const bo = Math.max(1, Number(this.format().bestOfLegs) || 3);
    return `Best of ${bo}`;
  },

  legStageLabel() {
    const n = Math.max(1, Number(this.liveScoring().currentLegNumber || 1));
    return `Leg ${n}`;
  },

  matchRaceLabel() {
    const need = this.legsNeededToWin();
    const a = this.legsWon('A');
    const b = this.legsWon('B');
    const max = Math.max(a, b);
    const left = Math.max(0, need - max);
    if (this.liveScoring().matchFinished) return 'Match over';
    if (left <= 0) return 'Deciding leg';
    return left === 1 ? '1 leg to win' : `${left} legs to win`;
  },

  boardTitle() {
    return `${this.participantName('A')} vs ${this.participantName('B')}`;
  },

  winnerSide() {
    const ls = this.liveScoring();
    if (ls.winnerSide) return String(ls.winnerSide);
    const need = this.legsNeededToWin();
    if (this.legsWon('A') >= need) return 'A';
    if (this.legsWon('B') >= need) return 'B';
    return '';
  },

  winnerDisplayName() {
    const side = this.winnerSide();
    return side ? this.participantName(side) : 'Winner';
  },

  syncMatchWinnerOverlay() {
    const show = this.kioskMode && !!this.liveScoring().matchFinished && !!this.winnerSide();
    this.matchWinnerOverlayVisible = show;
  },

  async loadBoard() {
    if (this.kioskMode && this._tvToken) {
      const tid = encodeURIComponent(this.tournamentId);
      const mid = encodeURIComponent(this.matchId);
      const url = `${apiAbsoluteUrl(`/tournaments/${tid}/matches/${mid}/tv-board`)}?tv_token=${encodeURIComponent(this._tvToken)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Board load failed');
      this.match = data.match || null;
      this.session = data.match?.session || null;
      return;
    }
    const base = `/tournaments/${encodeURIComponent(this.tournamentId)}/matches/${encodeURIComponent(this.matchId)}`;
    const res = await apiFetch(base);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Load failed');
    this.match = data.match || null;
    this.session = data.match?.session || null;
  },

  async refreshBoard() {
    try {
      await this.loadBoard();
      this.syncMatchWinnerOverlay();
      Alpine.nextTick(() => this.maybeCelebrate180());
    } catch { /* ignore poll errors */ }
  },

  boardEventUrl() {
    const tid = encodeURIComponent(this.tournamentId);
    const mid = encodeURIComponent(this.matchId);
    if (this.kioskMode && this._tvToken) {
      return `${apiAbsoluteUrl(`/tournaments/${tid}/matches/${mid}/tv-events`)}?tv_token=${encodeURIComponent(this._tvToken)}`;
    }
    const token = encodeURIComponent(
      (() => {
        try {
          return globalThis.localStorage?.getItem('dm.admin.access_token') || '';
        } catch {
          return '';
        }
      })(),
    );
    return `${apiAbsoluteUrl(`/tournaments/${tid}/matches/${mid}/session/events`)}?access_token=${token}`;
  },

  connectEventStream() {
    this.disconnectEventStream();
    if (!globalThis.EventSource) {
      this._pollTimer = globalThis.setInterval(() => this.refreshBoard(), 2500);
      return;
    }
    try {
      const es = new EventSource(this.boardEventUrl());
      this.eventSource = es;
      es.addEventListener('session:update', (ev) => {
        try {
          const payload = JSON.parse(ev.data || '{}');
          if (payload.session) {
            this.session = payload.session;
            this.eventStreamHealthy = true;
            this.syncMatchWinnerOverlay();
            Alpine.nextTick(() => this.maybeCelebrate180());
          }
        } catch { /* ignore */ }
      });
      es.onerror = () => { this.eventStreamHealthy = false; };
      es.onopen = () => { this.eventStreamHealthy = true; };
    } catch {
      this._pollTimer = globalThis.setInterval(() => this.refreshBoard(), 2500);
    }
  },

  disconnectEventStream() {
    if (this.eventSource) {
      try { this.eventSource.close(); } catch { /* ignore */ }
      this.eventSource = null;
    }
    if (this._pollTimer) {
      globalThis.clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  },

  ensureTv180Seen() {
    if (!this._tv180Seen) this._tv180Seen = Object.create(null);
  },

  flatten180Visits() {
    const out = [];
    for (const leg of this.liveScoring().legs || []) {
      const legN = Number(leg.legNumber || leg.number) || 1;
      const visits = leg.visits || [];
      for (let i = 0; i < visits.length; i += 1) {
        const v = visits[i];
        if (v?.bust) continue;
        if (Number(v.score) !== 180) continue;
        const role = String(v.playerRole || 'A').toUpperCase();
        out.push({
          key: `t|l${legN}|i${i}|${role}`,
          visit: v,
          role,
        });
      }
    }
    return out;
  },

  playerFor180(role) {
    return {
      name: this.participantName(role === 'B' ? 'B' : 'A'),
      avatar: '/assets/placeholder-player.png',
    };
  },

  seedTv180Baseline() {
    if (!this.kioskMode || this._tv180BaselineSeeded) return;
    this.ensureTv180Seen();
    for (const { key } of this.flatten180Visits()) {
      this._tv180Seen[key] = 1;
    }
    this._tv180BaselineSeeded = true;
  },

  maybeCelebrate180() {
    if (!this.kioskMode || !this._tv180BaselineSeeded) return;
    this.ensureTv180Seen();
    const list = this.flatten180Visits();
    const fresh = list.filter((x) => !this._tv180Seen[x.key]);
    for (const { key } of fresh) this._tv180Seen[key] = 1;
    if (!fresh.length) return;
    const last = fresh[fresh.length - 1];
    const { name, avatar } = this.playerFor180(last.role);
    this.tv180Name = name;
    this.tv180Avatar = avatar;
    this.tv180Active = true;
    fireTv180Confetti();
    if (this._tv180CloseTimer) globalThis.clearTimeout(this._tv180CloseTimer);
    this._tv180CloseTimer = globalThis.setTimeout(() => {
      this.tv180Active = false;
      this._tv180CloseTimer = null;
    }, 5500);
  },

  toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  },

  close() {
    globalThis.close();
  },
}));
